import { isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, OnDestroy } from '@angular/core';
import { Component, ElementRef, Inject, Input, NgZone, PLATFORM_ID, ViewChild } from '@angular/core';

import type { Image } from '@core-mkt/interfaces/image';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';
import { reviewData } from '../review-data';

@Component({
  selector: 'carousel-reviews',
  templateUrl: './carousel-reviews.component.html',
  styleUrls: ['./carousel-reviews.component.scss'],
  standalone: true,
  imports: [],
})
export class CarouselReviewsComponent implements AfterViewInit, OnDestroy {
  @ViewChild('swiperContainer', { static: false }) swiperContainer!: ElementRef<HTMLElement>;
  /** Craft sends either an image entry or a background-colour class string. */
  @Input() backgroundInfo: Image | string;
  @Input() backupColor: string;
  @Input() backgroundColorValue: string;
  @Input() textColorValue: string;
  @Input() textColorOverride: string;
  @Input() containerClasses: string;
  @Input() title: string;

  sliderReviews = reviewData[1];
  private swiper?: Swiper;

  // Swiper pagination and navigation require unique names in case there is more than 1 slider of the same type on a page
  nextSlide = 'swiper-button-next-' + Math.random().toString(16).slice(2);
  prevSlide = 'swiper-button-prev-' + Math.random().toString(16).slice(2);

  public swiperConfig: SwiperOptions = {
    modules: [Pagination, Navigation],
    // The removed swiper/angular wrapper always set observer: true; Swiper 12 defaults it to
    // false. Restored so slides re-rendered after init are still picked up, matching v8.
    observer: true,
    pagination: {
      type: 'fraction',
      renderFraction: function (currentClass, totalClass) {
        return '<span class="' + currentClass + '"></span>' + ' of ' + '<span class="' + totalClass + '"></span>';
      },
    },
    navigation: {
      nextEl: `.${this.nextSlide}`,
      prevEl: `.${this.prevSlide}`,
    },
    loop: true,
  };

  constructor(@Inject(PLATFORM_ID) private platformId: object, private ngZone: NgZone) {}

  get backgroundImageUrl(): string | null {
    return typeof this.backgroundInfo === 'object' && this.backgroundInfo ? this.backgroundInfo.url ?? null : null;
  }

  get backgroundClass(): string {
    return this.backgroundImageUrl
      ? `${this.textColorOverride} bg-no-repeat bg-center bg-cover`
      : (this.backgroundInfo as string) ?? '';
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId) || !this.swiperContainer) return;
    // Swiper is constructed outside Angular's zone. The removed swiper/angular wrapper did
    // the same (swiper_angular.mjs), and it matters here: Swiper 12 registers pointermove /
    // touchmove listeners on `document`, and this app runs provideZoneChangeDetection() with
    // no event coalescing -- in-zone, every mouse move anywhere on the page would run a full
    // change-detection tick per carousel. Nothing in this component needs Angular to re-render
    // in response to a Swiper event.
    this.ngZone.runOutsideAngular(() => {
      this.swiper = new Swiper(this.swiperContainer.nativeElement, this.swiperConfig);
    });
  }

  ngOnDestroy(): void {
    this.swiper?.destroy(true, true);
    this.swiper = undefined;
  }
}
